<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="640px">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="110px"
    >
      <el-form-item label="品牌" prop="moduleCode">
        <el-select
          v-model="formData.moduleCode"
          filterable
          clearable
          placeholder="请选择品牌"
          class="!w-full"
          :disabled="formType === 'update'"
        >
          <el-option
            v-for="dict in getStrDictOptions(DICT_TYPE.SYSTEM_LOGIN_SKIN_BRAND)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
        <div class="mt-6px text-12px text-[var(--el-text-color-secondary)]">
          BOSS直卖→商城；BOSS圈儿→AI商务社交；紫金山AI→活动/赛事/餐饮/旅游等共用
        </div>
      </el-form-item>
      <el-form-item label="绑定模块" prop="boundModuleCodes">
        <el-select
          v-model="formData.boundModuleCodes"
          multiple
          filterable
          clearable
          collapse-tags
          collapse-tags-tooltip
          placeholder="请选择归属该品牌的业务模块"
          class="!w-full"
        >
          <el-option
            v-for="item in moduleOptions"
            :key="item.code"
            :label="`${item.title}（${item.code}）`"
            :value="item.code"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="主标题" prop="title">
        <el-input v-model="formData.title" maxlength="64" show-word-limit placeholder="欢迎登录" />
      </el-form-item>
      <el-form-item label="副标题" prop="subtitle">
        <el-input
          v-model="formData.subtitle"
          maxlength="128"
          show-word-limit
          placeholder="登录后开启智能高效之旅"
        />
      </el-form-item>
      <el-form-item label="Logo" prop="logoUrl">
        <UploadImg v-model="formData.logoUrl" directory="system/login-skin" height="80px" width="80px" />
      </el-form-item>
      <el-form-item label="登录背景" prop="loginBgUrl">
        <UploadImg
          v-model="formData.loginBgUrl"
          directory="system/login-skin"
          height="90px"
          width="160px"
        />
      </el-form-item>
      <el-form-item label="主按钮色" prop="buttonColor">
        <div class="flex items-center gap-12px">
          <el-color-picker v-model="formData.buttonColor" color-format="hex" />
          <el-input v-model="formData.buttonColor" class="!w-140px" placeholder="#2B78FF" />
        </div>
      </el-form-item>
      <el-form-item label="链接文案色" prop="linkColor">
        <div class="flex items-center gap-12px">
          <el-color-picker v-model="formData.linkColor" color-format="hex" />
          <el-input v-model="formData.linkColor" class="!w-140px" placeholder="#2B78FF" />
        </div>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.SYSTEM_LOGIN_SKIN_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions, getStrDictOptions } from '@/utils/dict'
import { UploadImg } from '@/components/UploadFile'
import { LoginSkinApi, type LoginSkinModuleOption, type LoginSkinVO } from '@/api/system/loginSkin'

defineOptions({ name: 'SystemLoginSkinForm' })

defineProps<{
  moduleOptions: LoginSkinModuleOption[]
}>()

const { t } = useI18n()
const message = useMessage()

const dialogVisible = ref(false)
const dialogTitle = ref('')
const formLoading = ref(false)
const formType = ref<'create' | 'update'>('create')
const formRef = ref()
const formData = ref<LoginSkinVO>({
  id: undefined,
  moduleCode: '',
  title: '欢迎登录',
  subtitle: '登录后开启智能高效之旅',
  logoUrl: '',
  loginBgUrl: '',
  buttonColor: '#2B78FF',
  linkColor: '#2B78FF',
  boundModuleCodes: [],
  status: 1
})
const formRules = reactive({
  moduleCode: [{ required: true, message: '品牌不能为空', trigger: 'change' }],
  boundModuleCodes: [
    { required: true, type: 'array', min: 1, message: '请至少绑定一个业务模块', trigger: 'change' }
  ],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
})

const emit = defineEmits(['success'])

const resetForm = () => {
  formData.value = {
    id: undefined,
    moduleCode: '',
    title: '欢迎登录',
    subtitle: '登录后开启智能高效之旅',
    logoUrl: '',
    loginBgUrl: '',
    buttonColor: '#2B78FF',
    linkColor: '#2B78FF',
    boundModuleCodes: [],
    status: 1
  }
  formRef.value?.resetFields()
}

const open = async (type: 'create' | 'update', id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增品牌登录皮肤' : '编辑品牌登录皮肤'
  formType.value = type
  resetForm()
  if (id) {
    formLoading.value = true
    try {
      const data = await LoginSkinApi.get({ id })
      formData.value = {
        ...formData.value,
        ...data,
        boundModuleCodes: data?.boundModuleCodes || [],
        // 与 getIntDictOptions 对齐，避免字符串导致单选无法选中
        status: data?.status == null ? 1 : Number(data.status)
      }
    } finally {
      formLoading.value = false
    }
  }
}

const submitForm = async () => {
  await formRef.value.validate()
  formLoading.value = true
  try {
    const payload: LoginSkinVO = {
      ...formData.value,
      boundModuleCodes: formData.value.boundModuleCodes || []
    }
    await LoginSkinApi.save(payload)
    message.success(t('common.updateSuccess'))
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}

defineExpose({ open })
</script>
